/**
 * Modes Lab.
 *
 * A mode is not a shape to memorise, and it is not "the major scale started in a
 * different place" — that description is true and useless, because it explains
 * the notes without explaining the sound. What makes Dorian *sound* like Dorian
 * is hearing a natural 6 over a held D. So the tonic drone is not a decoration
 * here; it is the teaching device, and comparison against a parallel mode on the
 * same tonic is how the single changed degree becomes audible.
 *
 * Every spelling comes from `theory/modes.ts`, computed from tonic plus formula.
 * Nothing on this screen stores accidentals per mode name, because that is the
 * error the brief calls out: D Dorian has none, E Dorian has two.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Score } from '../components/Score';
import { Button, IconButton } from '../design/Button';
import { Card } from '../design/Feedback';
import { SegmentedControl, TabPanel, Tabs } from '../design/Navigation';
import { playPitches, playPitch, stop } from '../audio/player';
import { pitchName, type Pitch } from '../theory/pitch';
import { COMMON_TONICS } from '../theory/tonics';
import {
  MODE_LIST,
  MODES,
  compareModes,
  modeAccidentals,
  parentMajor,
  spellMode,
  tonicSeventh,
  tonicTriad,
  type ModeId,
} from '../theory/modes';
import { repositories } from '../storage/repositories';

const MASTERY_KEY = 'modes-lab';

interface ModeMastery {
  /** Mode ids the learner has identified correctly by ear, at least once. */
  heard: string[];
}

export function ModesLab() {
  const [tonicIndex, setTonicIndex] = useState(0);
  const [modeId, setModeId] = useState<ModeId>('dorian');
  const [workspace, setWorkspace] = useState<'sound' | 'harmony' | 'practice'>('sound');
  const [harmonyContext, setHarmonyContext] = useState<'modal' | 'functional'>('modal');
  const [compareWith, setCompareWith] = useState<ModeId>('aeolian');
  const [droning, setDroning] = useState(false);
  const [quiz, setQuiz] = useState<{ answer: ModeId; choices: ModeId[] } | null>(null);
  const [verdict, setVerdict] = useState<string | null>(null);
  const [mastery, setMastery] = useState<ModeMastery>({ heard: [] });
  const droneTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  const tonic = COMMON_TONICS[tonicIndex].pitch;
  const info = MODES[modeId];
  const scale = useMemo(() => spellMode(tonic, modeId), [tonic, modeId]);
  const accidentals = useMemo(() => modeAccidentals(tonic, modeId), [tonic, modeId]);
  const parent = useMemo(() => parentMajor(tonic, modeId), [tonic, modeId]);
  const triad = useMemo(() => tonicTriad(tonic, modeId), [tonic, modeId]);
  const seventh = useMemo(() => tonicSeventh(tonic, modeId), [tonic, modeId]);
  const differing = useMemo(() => compareModes(modeId, compareWith), [modeId, compareWith]);
  const contextualDegrees = useMemo(
    () => new Set(info.contextual.filter((tone) => tone.inContext === harmonyContext).map((tone) => tone.degree)),
    [info, harmonyContext],
  );
  const availableTensions = useMemo(
    () => info.tensions.filter((tone) => !contextualDegrees.has(tone.degree)),
    [info, contextualDegrees],
  );
  const useWithCare = useMemo(
    () => [
      ...info.avoid.map((tone) => ({
        degree: tone.degree,
        label: tone.label,
        reason: tone.clash,
        resolvesTo: tone.resolvesTo,
        practice: null as string | null,
      })),
      ...info.contextual
        .filter((tone) => tone.inContext === harmonyContext)
        .map((tone) => ({
          degree: tone.degree,
          label: tone.label,
          reason: tone.reason,
          resolvesTo: null,
          practice: tone.practice,
        })),
    ],
    [info, harmonyContext],
  );

  // --- mastery, persisted like every other record -------------------------
  useEffect(() => {
    let cancelled = false;
    void repositories()
      .settings.get(MASTERY_KEY)
      .then((row) => {
        if (cancelled || !row) return;
        const value = row.value as Partial<ModeMastery>;
        if (Array.isArray(value.heard)) setMastery({ heard: value.heard });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const remember = useCallback((id: ModeId) => {
    setMastery((current) => {
      if (current.heard.includes(id)) return current;
      const next = { heard: [...current.heard, id] };
      void repositories().settings.set(MASTERY_KEY, next as unknown as Record<string, unknown>);
      return next;
    });
  }, []);

  // --- sound ---------------------------------------------------------------
  const stopDrone = useCallback(() => {
    if (droneTimer.current) clearInterval(droneTimer.current);
    droneTimer.current = null;
    setDroning(false);
    stop();
  }, []);

  useEffect(() => stopDrone, [stopDrone]);
  // Changing tonic or mode mid-drone would leave the old tonic sounding under
  // the new scale, which is precisely the wrong lesson.
  useEffect(() => stopDrone, [tonicIndex, modeId, stopDrone]);

  const startDrone = () => {
    setDroning(true);
    const low: Pitch = { ...tonic, octave: tonic.octave - 1 };
    void playPitch(low, 3.2);
    droneTimer.current = setInterval(() => void playPitch(low, 3.2), 3000);
  };

  const playScale = (direction: 'up' | 'down', bpm = 96) => {
    const notes = direction === 'up' ? scale : [...scale].reverse();
    const octave: Pitch = { ...tonic, octave: tonic.octave + 1 };
    void playPitches(direction === 'up' ? [...notes, octave] : [octave, ...notes], 'arpeggio', bpm);
  };

  // --- ear practice --------------------------------------------------------
  const askQuiz = () => {
    const answer = MODE_LIST[Math.floor(Math.random() * MODE_LIST.length)].id;
    const pool = MODE_LIST.filter((mode) => mode.id !== answer).map((mode) => mode.id);
    const decoys = pool.sort(() => Math.random() - 0.5).slice(0, 2);
    setQuiz({ answer, choices: [answer, ...decoys].sort(() => Math.random() - 0.5) });
    setVerdict(null);
    const notes = spellMode(tonic, answer);
    void playPitches([...notes, { ...tonic, octave: tonic.octave + 1 }], 'arpeggio', 108);
  };

  const answerQuiz = (choice: ModeId) => {
    if (!quiz) return;
    const correct = choice === quiz.answer;
    setVerdict(
      correct
        ? `Yes — ${MODES[quiz.answer].name}.`
        : `That was ${MODES[quiz.answer].name}. ${MODES[quiz.answer].cause}`,
    );
    if (correct) remember(quiz.answer);
    setQuiz(null);
  };

  return (
    <div className="feature modes">
      <header className="feature-head">
        <div>
          <p className="eyebrow">Modes</p>
          <h2>Seven notes. One note feels like home.</h2>
          <p className="lede">
            Pick a home note and a mode. Hear the notes that make its sound.
          </p>
        </div>
      </header>

      {/* --- choose ---------------------------------------------------- */}
      <Card title="Tonic and mode">
        <div
          className="modes__tonics"
          role="radiogroup"
          aria-label="Tonic"
        >
            {COMMON_TONICS.map((entry, index) => (
            <button
              key={entry.label}
              type="button"
              role="radio"
              aria-checked={index === tonicIndex}
              className={`modes__tonic ${index === tonicIndex ? 'is-selected' : ''}`}
              onClick={() => setTonicIndex(index)}
            >
              {entry.label}
            </button>
          ))}
        </div>

        <div className="modes__modes" role="radiogroup" aria-label="Mode">
          {MODE_LIST.map((mode) => (
            <button
              key={mode.id}
              type="button"
              role="radio"
              aria-checked={mode.id === modeId}
              className={`modes__mode ${mode.id === modeId ? 'is-selected' : ''}`}
              onClick={() => setModeId(mode.id)}
            >
              <span className="modes__mode-name">{mode.name}</span>
              {/* Not colour alone: mastered modes carry a mark. */}
              {mastery.heard.includes(mode.id) ? (
                <span className="modes__mode-badge" title="Identified by ear">
                  ✓
                </span>
              ) : null}
            </button>
          ))}
        </div>
      </Card>

      <Tabs
        label="Mode workspace"
        idPrefix="modes-workspace"
        value={workspace}
        onChange={setWorkspace}
        options={[
          { value: 'sound', label: 'Sound', hint: 'Notes and color' },
          { value: 'harmony', label: 'Harmony', hint: 'Tensions and care notes' },
          { value: 'practice', label: 'Practice', hint: 'Compare and identify' },
        ]}
      />

      {/* --- the syntax card -------------------------------------------- */}
      <TabPanel idPrefix="modes-workspace" value="sound" active={workspace === 'sound'}>
      <Card title={`${COMMON_TONICS[tonicIndex].label} ${info.name}`}>
        <dl className="modes__facts">
          <div>
            <dt>Degrees</dt>
            <dd className="modes__formula">
              {info.formula.map((degree, index) => (
                <span
                  key={degree}
                  className={
                    info.characteristic.includes(index + 1) ? 'modes__degree is-characteristic' : 'modes__degree'
                  }
                >
                  {degree}
                </span>
              ))}
            </dd>
          </div>
          <div>
            <dt>Steps</dt>
            <dd>{info.steps.join(' · ')}</dd>
          </div>
          <div>
            <dt>Notes</dt>
            <dd>{scale.map((note) => pitchName(note).replace(/\d/g, '')).join(' ')}</dd>
          </div>
          <div>
            <dt>Accidentals</dt>
            <dd>
              {accidentals.length === 0
                ? 'None'
                : accidentals.map((note) => pitchName(note).replace(/\d/g, '')).join(' · ')}
            </dd>
          </div>
          <div>
            <dt>Same notes as</dt>
            {/* "Parent major", never "its own major key" — the distinction the
                brief singles out. E Dorian borrows D major, not E major. */}
            <dd>
              {pitchName(parent).replace(/\d/g, '')} major
              {info.id === 'ionian' ? ' (itself)' : ''}
            </dd>
          </div>
          <div>
            <dt>Tonic chord</dt>
            <dd>
              {triad.map((note) => pitchName(note).replace(/\d/g, '')).join(' ')} — {info.tonicTriad}
            </dd>
          </div>
        </dl>

        <p className="modes__cause">
          <strong>Why it sounds this way:</strong> {info.cause}
        </p>
        <p className="modes__association">{info.association}</p>

        <Score
          clef="treble"
          groups={scale.map((note) => [note])}
          labels={info.formula}
        />

        <div className="modes__actions">
          <Button variant="primary" icon="play" onClick={() => playScale('up')}>
            Hear it
          </Button>
          <Button variant="secondary" onClick={() => playScale('down')}>
            Descending
          </Button>
          <Button variant="secondary" onClick={() => playScale('up', 56)}>
            Slowly
          </Button>
          <Button
            variant={droning ? 'destructive' : 'secondary'}
            onClick={() => (droning ? stopDrone() : startDrone())}
          >
            {droning ? 'Stop drone' : 'Hold the tonic'}
          </Button>
          <Button
            variant="tertiary"
            onClick={() => void playPitches(seventh, 'block')}
          >
            Tonic seventh
          </Button>
        </div>
      </Card>
      </TabPanel>

      {/* --- chord tones, tensions, and avoid notes -------------------- */}
      <TabPanel idPrefix="modes-workspace" value="harmony" active={workspace === 'harmony'}>
      <Card title="Build color on the chord">
        <p className="modes__note">
          Chord tones are home. Tensions add color. “Use with care” means do not hold the note
          without hearing where it goes. You can still use it as a quick passing note.
        </p>
        <div className="modes__context">
          <div>
            <p className="eyebrow">Harmony around the chord</p>
            <p className="modes__context-copy">
              {harmonyContext === 'modal'
                ? 'The chord stays home. Hear the mode as one open sound.'
                : 'The chord moves to V7. This is the view many harmony classes use for ii–V.'}
            </p>
          </div>
          <SegmentedControl
            label="Harmony context"
            value={harmonyContext}
            onChange={(value) => setHarmonyContext(value as 'modal' | 'functional')}
            options={[
              { value: 'modal', label: 'Modal sound' },
              { value: 'functional', label: 'Moving to V7' },
            ]}
          />
        </div>
        <div className="modes__harmony-grid">
          <section className="modes__harmony-part" aria-labelledby="mode-foundation">
            <p className="eyebrow" id="mode-foundation">The base</p>
            <h3>{COMMON_TONICS[tonicIndex].label}{info.chordSuffix}</h3>
            <p className="modes__formula">1 · {info.formula[2]} · {info.formula[4]} · {info.formula[6]}</p>
            <p>{seventh.map((note) => pitchName(note)).join(' · ')}</p>
            <Button variant="tertiary" icon="play" onClick={() => void playPitches(seventh, 'block')}>
              Hear the chord
            </Button>
          </section>

          <section className="modes__harmony-part" aria-labelledby="mode-flavor">
            <p className="eyebrow" id="mode-flavor">Add color</p>
            <h3>{availableTensions.length ? availableTensions.map((tone) => tone.label).join(' · ') : 'No held color notes'}</h3>
            {availableTensions.map((tone) => {
              const note = scale[tone.degree - 1];
              return (
                <div className="modes__tone" key={tone.label}>
                  <p><strong>{tone.label} is {pitchName(note)}.</strong> {tone.reason}</p>
                  <Button
                    variant="tertiary"
                    icon="play"
                    onClick={() => void playPitches([...seventh, { ...note, octave: note.octave + 1 }], 'block')}
                  >
                    Hear {tone.label}
                  </Button>
                </div>
              );
            })}
          </section>

          <section className="modes__harmony-part is-care" aria-labelledby="mode-care">
            <p className="eyebrow" id="mode-care">Use with care</p>
            <h3>{useWithCare.length ? useWithCare.map((tone) => tone.label).join(' · ') : 'None in this view'}</h3>
            {useWithCare.length ? useWithCare.map((tone) => {
              const note = scale[tone.degree - 1];
              const home = tone.resolvesTo ? scale[tone.resolvesTo - 1] : null;
              return (
                <div className="modes__tone" key={`${harmonyContext}-${tone.label}`}>
                  <p><strong>{tone.label} is {pitchName(note)}.</strong> {tone.reason}</p>
                  {tone.practice ? <p className="modes__practice-tip"><strong>Try it:</strong> {tone.practice}</p> : null}
                  <Button
                    variant="tertiary"
                    icon="play"
                    onClick={() =>
                      void playPitches(
                        home ? [note, home] : [...seventh, { ...note, octave: note.octave + 1 }],
                        home ? 'arpeggio' : 'block',
                        72,
                      )
                    }
                  >
                    {home ? `Hear it move to ${tone.resolvesTo}` : `Hear ${tone.label} in context`}
                  </Button>
                </div>
              );
            }) : <p>No held note needs a warning in this view.</p>}
          </section>
        </div>
        <Score
          clef="treble"
          groups={[
            seventh,
            ...availableTensions.map((tone) => [scale[tone.degree - 1]]),
            ...useWithCare.map((tone) => [scale[tone.degree - 1]]),
          ]}
          labels={[
            `${COMMON_TONICS[tonicIndex].label}${info.chordSuffix}`,
            ...availableTensions.map((tone) => tone.label),
            ...useWithCare.map((tone) => `${tone.label} care`),
          ]}
        />
      </Card>
      </TabPanel>

      {/* --- comparison -------------------------------------------------- */}
      <TabPanel idPrefix="modes-workspace" value="practice" active={workspace === 'practice'}>
      <Card title="Compare on the same tonic">
        <p className="modes__note">
          Keep the home note still. Change the mode. Now you can hear the notes that moved.
        </p>
        <SegmentedControl
          label="Compare with"
          value={compareWith}
          onChange={(value) => setCompareWith(value as ModeId)}
          options={MODE_LIST.map((mode) => ({ value: mode.id, label: mode.name }))}
        />
        <p className="modes__difference">
          {differing.length === 0 ? (
            <>Those are the same mode.</>
          ) : (
            <>
              {info.name} and {MODES[compareWith].name} differ at degree
              {differing.length === 1 ? ' ' : 's '}
              <strong>{differing.join(' and ')}</strong>:{' '}
              {differing
                .map(
                  (degree) =>
                    `${info.formula[degree - 1]} against ${MODES[compareWith].formula[degree - 1]}`,
                )
                .join(', ')}
              .
            </>
          )}
        </p>
        <div className="modes__actions">
          <Button variant="secondary" onClick={() => playScale('up')}>
            Hear {info.name}
          </Button>
          <Button
            variant="secondary"
            onClick={() =>
              void playPitches(
                [...spellMode(tonic, compareWith), { ...tonic, octave: tonic.octave + 1 }],
                'arpeggio',
                96,
              )
            }
          >
            Hear {MODES[compareWith].name}
          </Button>
        </div>
      </Card>

      {/* --- ear practice ------------------------------------------------ */}
      <Card title="Name what you hear">
        <p className="modes__note">
          Played on {COMMON_TONICS[tonicIndex].label}, so the only thing that changes between questions is
          the mode.
        </p>
        {quiz ? (
          <div className="modes__quiz" role="group" aria-label="Mode choices">
            {quiz.choices.map((choice) => (
              <Button key={choice} variant="secondary" onClick={() => answerQuiz(choice)}>
                {MODES[choice].name}
              </Button>
            ))}
            <IconButton
              icon="play"
              label="Play it again"
              onClick={() =>
                void playPitches(
                  [...spellMode(tonic, quiz.answer), { ...tonic, octave: tonic.octave + 1 }],
                  'arpeggio',
                  108,
                )
              }
            />
          </div>
        ) : (
          <Button variant="primary" onClick={askQuiz}>
            Play a mode
          </Button>
        )}
        {verdict ? (
          <p className="modes__verdict" role="status">
            {verdict}
          </p>
        ) : null}
        <p className="modes__mastery">
          Identified by ear: {mastery.heard.length} of {MODE_LIST.length}
        </p>
      </Card>
      </TabPanel>
    </div>
  );
}
